import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { MapPin, Briefcase, Building2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

// Public page — no auth. Backend gates on ENABLE_CAREERS_SITE (404 when off).

export type PublicJob = {
  id: number
  code: string
  slug: string
  title: string
  team_name: string
  employment_type: string
  location_type: string
  location_country: string
  location_city: string
  department_name: string | null
  tags: string[]
  published_at: string
  expires_at: string | null
}

export const EMPLOYMENT_LABELS: Record<string, string> = {
  FULL_TIME: 'Full-time', PART_TIME: 'Part-time', CONTRACTOR: 'Contractor', INTERN: 'Intern',
}
export const LOCATION_LABELS: Record<string, string> = {
  ONSITE: 'On-site', REMOTE: 'Remote', HYBRID: 'Hybrid',
}

export function jobLocation(j: PublicJob): string {
  const place = [j.location_city, j.location_country].filter(Boolean).join(', ')
  const mode = LOCATION_LABELS[j.location_type] || j.location_type
  return place ? `${place} · ${mode}` : mode
}

export default function CareersList() {
  const [locType, setLocType] = useState<string>('')
  const [empType, setEmpType] = useState<string>('')

  const { data: jobs, isLoading, isError } = useQuery<PublicJob[]>({
    queryKey: ['careers-jobs', locType, empType],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (locType) params.location_type = locType
      if (empType) params.employment_type = empType
      const r = await axios.get('/careers/api/jobs/', { params })
      return r.data
    },
  })

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto max-w-4xl px-4 py-10">
          <h1 className="text-3xl font-bold tracking-tight">Open positions</h1>
          <p className="mt-2 text-muted-foreground">Join the team — browse our current openings below.</p>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex flex-wrap gap-2">
          {['', 'REMOTE', 'HYBRID', 'ONSITE'].map((v) => (
            <button key={v || 'all'} onClick={() => setLocType(v)}
              className={`rounded-full border px-3 py-1 text-sm ${locType === v ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>
              {v ? LOCATION_LABELS[v] : 'All locations'}
            </button>
          ))}
          <span className="mx-2" />
          {['', 'FULL_TIME', 'PART_TIME', 'CONTRACTOR', 'INTERN'].map((v) => (
            <button key={v || 'all'} onClick={() => setEmpType(v)}
              className={`rounded-full border px-3 py-1 text-sm ${empType === v ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>
              {v ? EMPLOYMENT_LABELS[v] : 'All types'}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-muted-foreground">Loading…</p>}
        {isError && <p className="text-muted-foreground">No open positions right now. Check back soon.</p>}

        <div className="space-y-3">
          {jobs?.map((j) => (
            <Link key={j.id} to={`/careers/jobs/${j.slug}`}
              className="block rounded-lg border p-4 transition-colors hover:bg-accent">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-semibold">{j.title}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {j.department_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{j.department_name}</span>}
                    <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{EMPLOYMENT_LABELS[j.employment_type] || j.employment_type}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{jobLocation(j)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-1">
                  {j.tags.slice(0, 4).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
                </div>
              </div>
            </Link>
          ))}
          {jobs && jobs.length === 0 && (
            <p className="py-12 text-center text-muted-foreground">No open positions match these filters.</p>
          )}
        </div>
      </main>
    </div>
  )
}
